function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

// Utility functions to handle body overflow and prevent content shift
function lockBodyScroll(breakpoint = null) {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  if (scrollbarWidth > 0) {
    // Set CSS variable for scrollbar width
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  // Add overflow-hidden class (with optional breakpoint)
  const className = breakpoint ? `overflow-hidden-${breakpoint}` : 'overflow-hidden';
  document.body.classList.add(className);
}

function unlockBodyScroll(breakpoint = null) {
  // Remove overflow-hidden class (with optional breakpoint)
  const className = breakpoint ? `overflow-hidden-${breakpoint}` : 'overflow-hidden';
  document.body.classList.remove(className);
  document.body.style.paddingRight = '';
  // Reset CSS variable
  document.documentElement.style.setProperty('--scrollbar-width', '0px');
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) {
    try {
      elementToFocus.focus({ preventScroll: true });
    } catch (error) {
      elementToFocus.focus();
    }
  }
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    lockBodyScroll(this.dataset.breakpoint);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    unlockBodyScroll(this.dataset.breakpoint);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);


class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;

    if (this.closest('.shopify-section')) {
      this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    }

    document.body.appendChild(this);
  }

  show(opener) {
    publish(PUB_SUB_EVENTS.modalToggle,  { display: false });
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    lockBodyScroll();
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    publish(PUB_SUB_EVENTS.modalToggle, { display: true });
    unlockBodyScroll();
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    console_log(this.id)

    if (this.id.includes('Gifts') && typeof CartItems !== 'undefined' && typeof CartItems.cartDrawerRenderContent === 'function') {
      CartItems.cartDrawerRenderContent();
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

class DayNightSwitcher extends HTMLElement {
  active = false;
  productInfoElement = undefined;
  dayElement = undefined;
  nightElement = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.productInfoElement = document.querySelector('product-info');
    this.dayElement = this.querySelector('.day__wrapper');
    this.nightElement = this.querySelector('.night__wrapper');

    this.dayElement.addEventListener('click', (e) => {
      e.preventDefault();

      if (!this.active) return;

      this.active = false;
      if (this.classList.contains('active')) this.classList.remove('active');

      publish(PUB_SUB_EVENTS.dayNightSwitcher, {
        switcher: this.dayElement.dataset.id
      });
    });

    this.nightElement.addEventListener('click', (e) => {
      e.preventDefault();

      if (this.active) return;

      this.active = true;
      if (!this.classList.contains('active')) this.classList.add('active');

      publish(PUB_SUB_EVENTS.dayNightSwitcher, {
        switcher: this.nightElement.dataset.id
      });
    });

    setTimeout(() => {
      publish(PUB_SUB_EVENTS.dayNightSwitcher, {
        switcher: this.dayElement.dataset.id
      });
    }, 500);
  }
}

customElements.define('day-night-switcher', DayNightSwitcher);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button') || this.querySelector('.div-button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = this.dataset.loop === 'true';
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    if (this.dataset.headerStyles) {
      try { this.headerStyles = JSON.parse(this.dataset.headerStyles); } catch (e) {}
      if (this.headerStyles) {
        this.headerEl = document.querySelector('.header-wrapper');
        this.addEventListener('slideChanged', (e) => {
          this.updateHeaderClass(e.detail.currentPage - 1);
          this.updateArrowsColor(e.detail.currentPage - 1);
        });
        this.updateHeaderClass(0);
        this.updateArrowsColor(0);
      }
    }
  }

  initPages() {
    const isMobile = !window.matchMedia('(min-width: 750px)').matches;
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => {
      if (isMobile && element.classList.contains('small-hide')) return false;
      return element.clientWidth > 0;
    });
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    const isNext = event.currentTarget.name === 'next';
    if (this.enableSliderLooping) {
      const lastItem = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
      const firstItem = this.sliderItemsToShow[0];
      if (isNext && this.isSlideVisible(lastItem)) {
        this.setSlidePosition(0);
        return;
      }
      if (!isNext && this.isSlideVisible(firstItem) && this.slider.scrollLeft <= 0) {
        this.setSlidePosition(lastItem.offsetLeft);
        return;
      }
    }
    this.slideScrollPosition = isNext
      ? this.slider.scrollLeft + step * this.sliderItemOffset
      : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  updateHeaderClass(index) {
    if (!this.headerEl || !this.headerStyles) return;
    this.headerStyles.forEach((cls) => { if (cls && cls !== '.') this.headerEl.classList.remove(cls); });
    const cls = this.headerStyles[index];
    if (!cls || cls === '.') return;

    // Light header icons only work over a transparent header on top of media.
    if (cls === 'header-color-light' && !this.headerEl.classList.contains('header-transparent')) {
      return;
    }

    this.headerEl.classList.add(cls);
  }

  // Same per-slide styles array used for the header (from the
  // dynamic_header_style_based_on_image_color metafield) also drives the
  // slider arrow icon color, since "header-color-light" indicates the
  // underlying image is dark at that slide.
  updateArrowsColor(index) {
    if (!this.headerStyles) return;
    const sliderButtons = this.querySelector('.slider-buttons');
    if (!sliderButtons) return;

    sliderButtons.classList.toggle('slider-buttons--arrows-white', this.headerStyles[index] === 'header-color-light');
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

function isGoogleAdsCampaignTraffic() {
  try {
    const kb = new URLSearchParams(window.location.search).get('kb') || '';
    return /^ga_(shp|shnb|pm)_/i.test(kb);
  } catch (e) {
    return false;
  }
}

function getUrlVariantId() {
  try {
    return new URLSearchParams(window.location.search).get('variant') || '';
  } catch (e) {
    return '';
  }
}

if (typeof window !== 'undefined') {
  window.isGoogleAdsCampaignTraffic = isGoogleAdsCampaignTraffic;
  window.getUrlVariantId = getUrlVariantId;
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.planPicker = null;
    this.variantTypePicker = null;
    this.packageOptions = null;
    this.merchActiveColor = null;
    this.merchColors = null;
  }

  connectedCallback() {
    this.planPicker =
      this.querySelector('.plan-picker, plan-picker-menobacs, plan-picker-v2-ab, plan-picker-september') || false;

    this.listenToDropdownSelector();

    this.variantTypePicker = this.querySelector('.variant-type-picker') || false;
    this.packageOptions = this.querySelectorAll('.variant-option_package') || false;
    this.merchActiveColor = this.querySelector('.merch-color .active-color-element') || false;
    this.merchColors = this.querySelectorAll('.merch-color .swatch-input__input-color') || false;

    if (this.planPicker) {
      this.planPicker.querySelectorAll('.plan-button').forEach(el => el.addEventListener('click', e => this.switchPurchaseType(e)));
      const urlVariantId = getUrlVariantId();
      const defaultType = this.planPicker.dataset.currentType;
      const initialType = this.getPurchaseTypeForVariantId(urlVariantId) || defaultType;
      // Swap to the 1st available option, if the onload selected variant - shouldn't be sold as a subscription
      this.activeProductOptionInfoToggle(initialType);
      if (initialType !== defaultType) {
        this.activePlanBtnToogle(initialType);
        this.updatePurchaseTypeInput(initialType);
        this.toggleProductFormInput(initialType);
      }

      if (!this.planPicker.classList.contains('plan-picker-v1-ab')) {
        this.renderPriceForSwitcherButton('subscription');
        this.renderPriceForSwitcherButton('onetimer');
      }
      this.v3renderLogic();
    }

    if (this.variantTypePicker) {
      this.variantTypePicker.querySelectorAll('.variant-type-button').forEach(el => {
        el.addEventListener('click', e => this.switchVariantType(e));
      });
      this.updateVariantTypePickerFromDefault();
      this.updateIngredientVisibility(this.variantTypePicker.dataset.currentType);
    }

    if (this.merchActiveColor && this.merchColors) {
      this.changeActiveColor();
    }

    const checkedInput = this.querySelector('fieldset input:checked');
    if (checkedInput) this.giftOptionsRender(checkedInput.value);

    this.addEventListener('change', (event) => {
      const target = event.target;
      this.publishOptionChangeEvent(PUB_SUB_EVENTS.optionValueSelectionChange, event, target, this.selectedOptionValues);

      this.giftOptionsRender(target.value);

      //separate logic 90/180 days
      if (this.classList.contains('separate-logic')) {
        if (this.planPicker?.dataset.currentType == 'onetimer') {
          let inputIds = document.querySelectorAll("product-form .product-variant-id");

          if (inputIds.length) {
            inputIds.forEach(input => {
              if (target.value == '90 days') {
                input.value = input.dataset["90"];
              } else {
                input.value = input.dataset["180"];
              }
            });
          }
        }
      }
    });

    const isStackedPicker =
      this.planPicker?.matches?.('plan-picker-menobacs, plan-picker-v2-ab, plan-picker-september') ||
      this.planPicker?.classList?.contains('plan-picker-menobacs') ||
      this.planPicker?.classList?.contains('plan-picker-september');
    if (this.planPicker && !isStackedPicker) {
      this.selectFirstVisible({ honorUrlVariant: true });
    }
  }

  giftOptionsRender(value) {
    const giftOptionWrapper = this.querySelector('.option__info-gifts-wrapper');
    const giftOptionItems = this.querySelectorAll('.option__info-gifts-wrapper .option__info-gifts');

    if (giftOptionWrapper && giftOptionItems) {
      giftOptionItems.forEach(item => {
        if (value.trim() == item.dataset.value.split('|').at(-1).trim()) {
          item.classList.remove('hidden');
        } else {
          item.classList.add('hidden');
        }
      });
    }
  }

  updatePlanButtonDetails(dropdown) {
    const planButton = dropdown.closest('.plan-button');
    if (!planButton) return;

    const planType = planButton.dataset.type;
    const selectedOption = dropdown.querySelector('.dropdown-option.selected');
    if (!selectedOption) return;

    const planPicker = planButton.closest('.plan-picker');
    const priceMode = planPicker?.dataset.priceMode || 'default';
    const price = priceMode === 'per_day' ? selectedOption.dataset.price : selectedOption.dataset.priceDefault;

    if (price) {
      const priceEl = planType === 'subscription'
        ? document.querySelector('#item_sub_price')
        : document.querySelector('#item_ot_price');

      if (priceEl) {
        if (priceMode === 'per_day') {
          const dayLabel = priceEl.dataset.dayLabel || 'Tag';
          priceEl.textContent = `${price}/${dayLabel}`;
        } else {
          priceEl.textContent = price;
        }
      }
    }

    const strikeEl = planType === 'subscription'
      ? document.querySelector('#item_sub_strike_price')
      : document.querySelector('#item_ot_strike_price');

    if (strikeEl) {
      const strikePrice = selectedOption.dataset.strikePrice;
      if (strikePrice) {
        strikeEl.textContent = strikePrice;
        strikeEl.style.display = '';
      } else {
        strikeEl.style.display = 'none';
      }
    }

    const packageText = selectedOption.dataset.package;
    if (packageText) {
      const packageEl = planButton.querySelector('.plan-package-text');
      if (packageEl) packageEl.textContent = packageText;
    }

    if (planType === 'subscription') {
      const discount = selectedOption.dataset.discount;
      const badgeEl = planButton.querySelector('.plan-button-discount-badge');
      if (badgeEl) {
        if (discount && Number(discount) > 0) {
          const saveLabel = badgeEl.textContent.split(/\d/)[0].trim();
          badgeEl.textContent = `${saveLabel} ${discount}%`;
        } else {
          badgeEl.style.display = 'none';
        }
      }
    }
  }

  listenToDropdownSelector() {
    const dropdowns = document.querySelectorAll('dropdown-selector');
    if (!dropdowns.length) return;

    dropdowns.forEach(dropdown => {
      dropdown.addEventListener('dropdown:select', (e) => {
        const planButton = dropdown.closest('.plan-button');
        if (!planButton) return;

        const isActive = planButton.classList.contains('active');
        if (!isActive) return;

        const selectedOptionName = e.detail.value;
        const planType = planButton.dataset.type;

        this.updatePlanButtonDetails(dropdown);

        const optionInfos = this.querySelectorAll(`.option__info[data-type="${planType}"]`);

        for (const optionInfo of optionInfos) {
          const nameEl = optionInfo.querySelector('.option__info-name span.h5');
          if (nameEl && nameEl.textContent.trim() === selectedOptionName) {
            const label = optionInfo.closest('.variant-option_package');
            if (!label) break;

            const inputId = label.getAttribute('for');
            const radioInput = inputId ? this.querySelector(`#${inputId}`) : null;

            if (radioInput) {
              radioInput.checked = true;

              this.publishOptionChangeEvent(
                PUB_SUB_EVENTS.optionValueSelectionChange,
                { target: radioInput },
                radioInput,
                this.selectedOptionValues
              );

              this.giftOptionsRender(radioInput.value);
            }
            break;
          }
        }
      });
    });
  }

  changeActiveColor() {
    this.merchColors.forEach(item => {
      item.addEventListener('click', () => {
        this.merchActiveColor.innerHTML = `
          <span class="fw-700 uppercase">${item.dataset.color} | </span>
          <span class="fw-400 uppercase">${item.dataset.sku}</span>
        `
      });
    });
  }

  publishOptionChangeEvent(eventName, event, target, selectedOptionValues){
    publish(eventName, {
      data: {
        event,
        target,
        selectedOptionValues: selectedOptionValues,
      },
    });
  }

  getProductFormEl() {
    const productInfo = this.closest('product-info');
    return (
      productInfo?.querySelector(`product-form[data-section-id="${this.dataset.section}"]`) ||
      productInfo?.querySelector('product-form') ||
      document.querySelector(`product-form[data-section-id="${this.dataset.section}"]`)
    );
  }

  lockSubmitUntilVariantSettled() {
    this.getProductFormEl()?.lockForVariantChange?.();
  }

  unlockSubmitIfVariantIdle() {
    setTimeout(() => {
      const productInfo = this.closest('product-info');
      if (productInfo?.pendingRequestUrl) return;
      this.getProductFormEl()?.unlockForVariantChangeIfIdle?.();
    }, 0);
  }

  queueVariantRefresh() {
    const checkedInput = this.querySelector('fieldset input:checked');
    if (!checkedInput) return;
    if (this.closest('product-info')?.pendingRequestUrl) return;

    this.publishOptionChangeEvent(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      { target: checkedInput },
      checkedInput,
      this.selectedOptionValues
    );
  }

  applyPurchaseType(type, {
    publishEvent = true,
    selectFirstVisible = true,
    toggleVariantTitles = true,
    forceRerender = false,
  } = {}) {
    if (!this.planPicker) return;
    if (!type) return;

    const normalizedType = String(type).toLowerCase();
    const allowedTypes = ['subscription', 'onetimer'];
    if (!allowedTypes.includes(normalizedType)) return;

    // Lock ATC before selling_plan / _purchase_type are mutated. A variant
    // refetch (if any) is often queued on the next tick; without this lock the
    // form can be submitted with a stale variant id.
    this.lockSubmitUntilVariantSettled();

    // Remember the checked input BEFORE toggling options so we can detect if
    // activeProductOptionInfoToggle auto-switched the variant for us (which
    // also publishes optionValueSelectionChange itself).
    const previouslyCheckedInput = this.querySelector('fieldset input:checked');

    this.toggleProductFormInput(normalizedType);
    this.activePlanBtnToogle(normalizedType);
    this.activeProductOptionInfoToggle(normalizedType);
    this.updatePurchaseTypeInput(normalizedType);

    if (toggleVariantTitles) {
      const variantTitlesWrapper = this.querySelectorAll('.variant-titles__wrapper .h5');
      if (variantTitlesWrapper.length) {
        variantTitlesWrapper.forEach(i => i.classList.toggle('hidden'));
      }
    }

    if (selectFirstVisible) {
      // Never re-lock to the URL variant when the shopper changes Abo/Einmalkauf.
      // `?variant=` is written after the first selection, so honouring it here
      // snaps classic/bundle/influencer pickers back to subscription.
      this.selectFirstVisible({ honorUrlVariant: false });
    }

    const giftOptionWrapper = this.querySelector('.option__info-gifts-wrapper');
    if (giftOptionWrapper) {
      normalizedType == 'onetimer' ? giftOptionWrapper.classList.add('hidden') : giftOptionWrapper.classList.remove('hidden');
    }

    // Toggle product-form button main price (reads selling_plan[disabled] which we just updated)
    this.getProductFormEl()?.togglePrices?.();

    // When rehydrating (selectFirstVisible=false), activeProductOptionInfoToggle only
    // publishes optionValueSelectionChange if it auto-switched the variant. If the
    // currently-selected variant is still valid for the new type, nothing would trigger
    // product-info to refetch. Force-publish the event so the price/markup gets re-rendered.
    if (forceRerender) {
      const stillCheckedInput = this.querySelector('fieldset input:checked');
      const nothingAutoSwitched = stillCheckedInput && stillCheckedInput === previouslyCheckedInput;
      if (nothingAutoSwitched) {
        this.publishOptionChangeEvent(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          { target: stillCheckedInput },
          stillCheckedInput,
          this.selectedOptionValues
        );
      }
    }

    if (publishEvent) {
      publish(PUB_SUB_EVENTS.purchaseTypeChange, { type: normalizedType });
    }

    // Classic ES picker (no v1/v2/v3 class) often never clicks a radio on tab
    // change, so the variant fetch never starts and ATC would unlock on the
    // next tick with a stale id. Always queue a refresh when nothing else did.
    this.queueVariantRefresh();
    this.unlockSubmitIfVariantIdle();
  }

  switchPurchaseType(event) {
    const type = event.target.closest('button').dataset.type;
    this.applyPurchaseType(type, {
      publishEvent: true,
      selectFirstVisible: true,
      toggleVariantTitles: true,
    });
  }

  updatePurchaseTypeInput(type) {
    const purchaseTypeInput = this.querySelector('input[name="properties[_purchase_type]"]');
    if (purchaseTypeInput) {
      purchaseTypeInput.value = type;
    }
  }

  getPurchaseTypeForVariantId(variantId) {
    if (!variantId) return null;

    const infos = this.querySelectorAll(
      `.option__info[data-variant-id="${CSS.escape(String(variantId))}"]`
    );
    if (!infos.length) return null;

    const preferred = this.planPicker?.dataset.currentType || 'subscription';
    const preferredInfo = Array.from(infos).find((info) => info.dataset.type === preferred);
    return (preferredInfo || infos[0]).dataset.type || null;
  }

  selectPackageByVariantId(variantId, visibleSelects) {
    if (!variantId) return false;

    const currentType = this.planPicker?.dataset.currentType;
    const match = Array.from(visibleSelects || []).find((item) =>
      Array.from(item.querySelectorAll('.option__info')).some((info) => {
        if (String(info.dataset.variantId) !== String(variantId)) return false;
        if (currentType && info.dataset.type && info.dataset.type !== currentType) return false;
        return true;
      })
    );

    if (match) {
      match.click();
      return true;
    }

    const preferredType = this.getPurchaseTypeForVariantId(variantId);
    const info = preferredType
      ? this.querySelector(
          `.option__info[data-type="${CSS.escape(preferredType)}"][data-variant-id="${CSS.escape(String(variantId))}"]`
        )
      : this.querySelector(`.option__info[data-variant-id="${CSS.escape(String(variantId))}"]`);
    const label = info?.closest('.variant-option_package');
    if (!label) return false;

    const type = info.dataset.type;
    if (type && this.planPicker && this.planPicker.dataset.currentType !== type) {
      this.applyPurchaseType(type, {
        publishEvent: true,
        selectFirstVisible: false,
        toggleVariantTitles: true,
      });
    }

    label.click();
    return true;
  }

  selectFirstVisible({ honorUrlVariant = false } = {}) {
    const visibleSelects = this.querySelectorAll('.variant-option_package:not(.hidden)');
    const variantParam = honorUrlVariant ? getUrlVariantId() : '';

    if (variantParam) {
      if (this.selectPackageByVariantId(variantParam, visibleSelects)) return;

      const checkedInput = this.querySelector('fieldset input:checked');
      if (checkedInput) {
        setTimeout(() => checkedInput.dispatchEvent(new Event('change', { bubbles: true })), 0);
      }
      return;
    }

    if (isGoogleAdsCampaignTraffic()) {
      if (visibleSelects.length) {
        visibleSelects[0].click();
      } else {
        const packageInputs = this.querySelectorAll(
          '.product-options_package input[type="radio"]:not(.hidden):not(.disabled)'
        );
        const firstInput = packageInputs[0];
        if (firstInput) {
          packageInputs.forEach((input) => {
            input.checked = false;
          });
          firstInput.checked = true;
          this.lockSubmitUntilVariantSettled();
          setTimeout(() => {
            firstInput.dispatchEvent(new Event('change', { bubbles: true }));
            this.unlockSubmitIfVariantIdle();
          }, 0);
        }
      }
      return;
    }

    if (this.dataset.currentSelect > 0) {
      const checkedInput = this.querySelector('fieldset input:checked');
      if (checkedInput) {
        this.lockSubmitUntilVariantSettled();
        setTimeout(() => {
          checkedInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.unlockSubmitIfVariantIdle();
        }, 0);
      }
    } else if ((this.classList.contains('v1') || this.classList.contains('v2') || this.classList.contains('v3')) && visibleSelects.length) {
      const preselected = Array.from(visibleSelects).find((item) => {
        const inputId = item.getAttribute('for');
        const input = inputId ? this.querySelector(`#${CSS.escape(inputId)}`) : item.querySelector('input');
        return input && input.checked;
      });
      (preselected || visibleSelects[0]).click();
    } else {
      const checkedInput = this.querySelector('fieldset input:checked');
      if (checkedInput) {
        checkedInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (visibleSelects.length) {
        visibleSelects[0].click();
      }
    }
  }

  updateVariantTypePickerFromDefault() {
    if (!this.variantTypePicker) return;
    
    // Find the checked input in .product-options_default
    const defaultOptions = this.querySelector('.product-options_default');
    if (!defaultOptions) return;
    
    const checkedInput = defaultOptions.querySelector('input[type="radio"]:checked');
    if (!checkedInput) return;
    
    // Get the value from the checked input
    const inputValue = checkedInput.value;
    if (!inputValue) return;
    const normalizedValue = inputValue.toLowerCase();

    // Match on data-type (always the raw option value, lowercased) rather than
    // data-label, which merchants can override to arbitrary display text via
    // type_picker_label_1/2 and would then never match the radio's raw value.
    const variantTypeButton = Array.from(this.variantTypePicker.querySelectorAll('.variant-type-button')).find(
      (btn) => btn.dataset.type?.toLowerCase() === normalizedValue
    );
    if (variantTypeButton) {
      // Remove active class from all buttons
      this.variantTypePicker.querySelectorAll('.variant-type-button').forEach(btn => {
        btn.classList.remove('active');
      });

      // Add active class to the matching button
      variantTypeButton.classList.add('active');
      this.variantTypePicker.dataset.currentType = variantTypeButton.dataset.type;

      // Update ingredient visibility
      this.updateIngredientVisibility(variantTypeButton.dataset.type);
    }
  }

  v3renderLogic() {
    let selectors = this.querySelectorAll('.option__info-selects__selector');

    if (selectors.length) {
      selectors.forEach(selector => {
        selector.addEventListener('click', e => {
          e.preventDefault();

          this.querySelector(`[for="${selector.dataset.link}"]`).click();
        });
      });

      //render discount
      selectors.forEach(selector => {
        let badge = selector.querySelector('.option__info-selects__selector-badge');
        let label = this.querySelector(`[for="${selector.dataset.link}"]`)

        console.log('#', selector.dataset.link);

        if (badge) {
          let variantDiscountItem = label.querySelector('.variant-discount-v3');

          console.log(variantDiscountItem);

          badge.innerHTML = variantDiscountItem.dataset.discount;
          badge.classList.remove('hidden');
        }
      });
    }
  }

  renderPriceForSwitcherButton(type) {
    // a/b v1 prices render in switcher
    const buttonOnetime = this.querySelector('.plan-picker-v1 #item_sub_price');
    const buttonSubscription = this.querySelector('.plan-picker-v1 #item_ot_price');
    const subscriptionButtonWrapper = this.querySelector('.plan-picker-v1 .plan-button[data-type="subscription"]');
    const badgeSubscriptions = this.querySelectorAll('.variant-discount-v1');

    if (buttonOnetime && buttonSubscription) {
      let lowestPrice = this.querySelectorAll(`.variant-option:not(.hidden-option) .option__info[data-type="${type}"] .variant-price span:first-child`);
      let currentTLD = window.location.hostname.split('.').pop();

      if (type == 'onetimer') {
        if (currentTLD == 'es') {
          if (buttonSubscription.dataset.tag == 'true') {
            buttonSubscription.innerHTML = lowestPrice[0].innerHTML;
          } else {
            if (lowestPrice[0].dataset.variant.includes('de 3')) {
              buttonSubscription.innerHTML = lowestPrice[0].innerHTML;
            } else {
              buttonSubscription.innerHTML = lowestPrice[1].innerHTML;
            }
          }

          subscriptionButtonWrapper.querySelector('#discount_percentage').innerText = badgeSubscriptions[1].dataset.discount;
        } else {
          buttonSubscription.innerHTML = lowestPrice[lowestPrice.length - 2].innerHTML;
          subscriptionButtonWrapper.querySelector('#discount_percentage').innerText = badgeSubscriptions[badgeSubscriptions.length - 1].dataset.discount;
        }
      }

      if (type == 'subscription') {
        if (currentTLD == 'es') {
          buttonOnetime.innerHTML = lowestPrice[1].innerHTML;
        } else {
          buttonOnetime.innerHTML = lowestPrice[lowestPrice.length - 2].innerHTML;
        }
      }
    }
  }

  toggleProductFormInput(type) {
    const productForm =
      document.getElementById(`product-form-${this.dataset.section}`) ||
      this.getProductFormEl()?.querySelector('form');
    const input = productForm?.querySelector('input[name="selling_plan"]');
    if (!input) return;

    if (type == 'onetimer'){
      input.setAttribute('disabled', 'disabled');
    } else {
      input.removeAttribute('disabled');
    }

    const giftsInput = productForm.querySelector('input[name="properties[_gifts]"][data-requires-subscription]');
    if (giftsInput) {
      // Products that combine a variant-type picker (e.g. protein flavor) with
      // a plan picker should trigger the gifts popup for every variant, so skip
      // the onetimer-disables-gifts rule for them.
      const giftsAlwaysEnabled = this.variantTypePicker && this.planPicker;
      if (type == 'onetimer' && !giftsAlwaysEnabled) {
        giftsInput.setAttribute('disabled', 'disabled');
      } else {
        giftsInput.removeAttribute('disabled');
      }
    }
  }

  activeProductOptionInfoToggle(type) {
    let purchaseOptionsInfo = this.querySelectorAll('.option__info') || false;
    // Hide-show option info description for onetimer/subscription
    purchaseOptionsInfo && purchaseOptionsInfo.forEach(info => {
      info.dataset.type == type ? info.classList.remove('hidden') : info.classList.add('hidden');
    });

    // Remember the currently-checked package input so we can decide if we really
    // need to auto-switch (the old code unconditionally switched to the next sibling
    // of the first package, which clobbered user selections like 180-day).
    const previouslyCheckedInput = this.querySelector('.product-options_package input:checked')
      || this.querySelector('fieldset input:checked');

    // First pass: toggle visibility of each package based on whether it has any
    // child descriptor matching the new type.
    this.packageOptions.forEach((option) => {
      const inputId = option.getAttribute('for');
      const input = inputId ? this.querySelector(`#${inputId}`) : option.previousElementSibling;
      if (option.querySelectorAll(`[data-type=${type}]`).length > 0) {
        option.classList.remove('hidden');
        if (input?.hasAttribute('disabled')) {
          input.removeAttribute('disabled');
          input.classList.remove('hidden', 'disabled');
        }
      } else {
        option.classList.add('hidden');
        if (input && !input.hasAttribute('disabled')) {
          input.setAttribute('disabled', 'disabled');
          input.classList.add('hidden', 'disabled');
        }
      }
    });

    // Determine whether the previously checked input's label is still visible.
    // Only if the current selection becomes invalid do we auto-switch to the
    // first visible package and publish optionValueSelectionChange.
    if (!previouslyCheckedInput) return;

    const previouslyCheckedLabel = previouslyCheckedInput.id
      ? this.querySelector(`label[for="${CSS.escape(previouslyCheckedInput.id)}"]`)
      : null;
    const isCurrentlyHidden = previouslyCheckedLabel
      ? previouslyCheckedLabel.classList.contains('hidden')
      : false;

    if (!isCurrentlyHidden) {
      // Current selection remains valid — do not touch the checked state or fire events.
      return;
    }

    const firstVisibleLabel = Array.from(this.packageOptions).find(
      (opt) => !opt.classList.contains('hidden')
    );
    if (!firstVisibleLabel) return;

    const firstVisibleInputId = firstVisibleLabel.getAttribute('for');
    const targetInput = firstVisibleInputId
      ? document.getElementById(firstVisibleInputId) || this.querySelector(`#${CSS.escape(firstVisibleInputId)}`)
      : firstVisibleLabel.previousElementSibling;
    if (!targetInput) return;

    previouslyCheckedInput.checked = false;
    targetInput.checked = true;

    this.publishOptionChangeEvent(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      { target: targetInput },
      targetInput,
      this.selectedOptionValues
    );
  }

  activePlanBtnToogle(type) {
    this.planPicker.setAttribute("data-current-type", type);

    this.planPicker.querySelectorAll('.plan-button').forEach(btn => {
      btn.dataset.type == type ? btn.classList.add('active') : btn.classList.remove('active');
    });

    this.querySelectorAll(".variant-titles__wrapper h1").forEach(item => {
      item.dataset.type == type ? item.classList.remove('hidden') : item.classList.add('hidden');
    });
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }

  get selectedCurrentOption() {
    return Array.from(this.querySelectorAll('fieldset input:checked')).map(
      ({ dataset }) => dataset.optionCurrent
    );
  }

  get selectedCurrentOptionOnetime() {
    return Array.from(this.querySelectorAll('fieldset input:checked')).map(
      ({ dataset }) => dataset.optionCurrentOnetime
    );
  }

  get isInfluencer() {
    return Array.from(this.querySelectorAll('fieldset input:checked')).map(
      ({ dataset }) => dataset.influencer
    );
  }

  switchVariantType(event) {
    const button = event.target.closest('.variant-type-button');
    if (!button) return;
    const type = button.dataset.type;
    const label = button.dataset.label;
    
    this.variantTypePicker.querySelectorAll('.variant-type-button').forEach(btn => {
      btn.classList.remove('active');
    });
    button.classList.add('active');
    
    this.variantTypePicker.dataset.currentType = type;

    const typeFieldset = this.querySelector('.product-options_default');

    let targetVariant = null;

    if (typeFieldset) {
      const radioInputs = typeFieldset.querySelectorAll('input[type="radio"]');

      for (const input of radioInputs) {
        const label = input.nextElementSibling;
        if (!label) continue;

        const labelText = label.textContent.toLowerCase();

        // Dynamic matching based on the selected type
        if (labelText.includes(type.toLowerCase())) {
          targetVariant = input;
          input.checked = true;
          break;
        }
      }
    }

    if (targetVariant) {
      targetVariant.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      console.log('No matching variant found for type:', type);
    }

    this.updateIngredientVisibility(type);
  }

  get getPlanPicker() {
    return this.planPicker;
  }

  updateIngredientVisibility(type) {
    const ingredientCards = document.querySelectorAll('.ingredient-card');
    const ingredientSlides = document.querySelectorAll('.ingredient-slide');
    
    if (ingredientCards.length > 0) {
      let visibleIndex = 0;
      ingredientCards.forEach(card => {
        const ingredientType = card.dataset.ingredientType;
        const opener = card.querySelector('.ingridient-card__opener');
        
        if (ingredientType === 'both' || ingredientType === type) {
          card.style.visibility = 'visible';
          card.style.opacity = '1';
          card.style.position = 'static';
          // Update the data-index to match the visible index
          if (opener) {
            opener.dataset.index = visibleIndex.toString();
          }
          visibleIndex++;
        } else {
          card.style.visibility = 'hidden';
          card.style.opacity = '0';
          card.style.position = 'absolute';
          card.style.left = '-9999px';
        }
      });
    }

    if (ingredientSlides.length > 0) {
      ingredientSlides.forEach(slide => {
        const ingredientType = slide.dataset.ingredientType;
        if (ingredientType === 'both' || ingredientType === type) {
          slide.style.visibility = 'visible';
          slide.style.opacity = '1';
          slide.style.position = 'static';
        } else {
          slide.style.visibility = 'hidden';
          slide.style.opacity = '0';
          slide.style.position = 'absolute';
          slide.style.left = '-9999px';
        }
      });
    }
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class Accordion {
  constructor(el) {
    this.el = el;
    this.summary = el.querySelector('summary');
    this.content = el.querySelector('.accordion__content');
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    this.summary.addEventListener('click', (e) => this.onClick(e));
  }

  onClick(e) {
    if (!e.target.closest('a')) e.preventDefault();
    this.el.style.overflow = 'hidden';

    if (this.isClosing || !this.el.open) {
      this.open();
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;
    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.el.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 200,
      easing: 'linear'
    });

    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
  }

  open() {
    this.el.style.height = `${this.el.offsetHeight}px`;
    this.el.open = true;

    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;
    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + this?.content?.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.el.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 200,
      easing: 'linear'
    });

    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
  }

  onAnimationFinish(open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = this.el.style.overflow = '';
  }
}

document.querySelectorAll('details').forEach((el) => {
  new Accordion(el);
});


window.isOverlayBlockingChat = () =>
  !!document.querySelector(
    '.pgwp-info-drawer.active, .mm-info-drawer.active, cart-drawer.active, details-modal[open], .variant-picker-popup:not(.hidden)'
  );

window.toggleChatIcon = (obj) => {
  const chat = document.querySelector('#gorgias-chat-container');

  if (!chat) return;

  const display = obj?.display;

  if (obj && obj.display !== undefined) {
    display ? chat.classList.remove('hidden') : chat.classList.add('hidden');
  } else if (window.isOverlayBlockingChat()) {
    chat.classList.add('hidden');
  } else {
    chat.classList.toggle('hidden');
  }
};

document.addEventListener('DOMContentLoaded', () => {
  subscribe(PUB_SUB_EVENTS.modalToggle, window.toggleChatIcon);

  const chatObserver = new MutationObserver(() => {
    if (window.isOverlayBlockingChat()) {
      window.toggleChatIcon({ display: false });
    }
  });

  chatObserver.observe(document.body, { childList: true, subtree: true });
});

class AppearenceObserver {
  static appear = (selector) => {
    return new Promise(resolve => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }

      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          resolve(document.querySelector(selector));
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        subtree: true,
        childList: true,
      });
    });
  }
}


class PasswordVisibilityToggler extends HTMLElement {
  constructor() {
    super();

    this.input = null;
  }

  connectedCallback() {
    this.input = this.previousElementSibling;
    this.addEventListener('click', this.toggleVisibility);
  }

  toggleVisibility() {
    this.input.type == 'password' ?
    this.input.setAttribute('type', 'text') : 
    this.input.setAttribute("type", "password")
  }
}

customElements.define('password-toggler', PasswordVisibilityToggler);


class ElementVisibilityToggler extends HTMLElement {
  constructor() {
    super();

    this.elToShow = null;
    this.elToHide = null;
  }

  connectedCallback() {
    this.elToShow = this.dataset.elToShow;
    this.elToHide = this.dataset.elToHide;

    this.addEventListener('click', this.toggleVisibility);
  }

  toggleVisibility() {
    document.querySelector(this.elToShow).classList.remove('hidden');
    document.querySelector(this.elToHide).classList.add('hidden');
  }
}

customElements.define('element-toggler', ElementVisibilityToggler);
